import { openDB } from 'idb'
import type { IDBPObjectStore } from 'idb'

import type { ChatSummary, MessageRecord, SearchResultRecord, SyncRangeRecord } from '../../shared/types'

type StoredChat = Omit<ChatSummary, 'syncedMessageCount' | 'syncedStorageBytes'> & {
  syncedMessageCount?: number
  syncedStorageBytes?: number
}
type StoredSearchDoc = MessageRecord

const DB_NAME = 'tg-search-lite'
const DB_VERSION = 3

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db, oldVersion, _newVersion, transaction) {
    if (oldVersion < 1) {
      const chats = db.createObjectStore('chats', { keyPath: 'id' })
      chats.createIndex('by-last-message', 'lastMessageAt')

      const messages = db.createObjectStore('messages', { keyPath: ['chatId', 'messageId'] })
      messages.createIndex('by-chat-date', ['chatId', 'dateTs'])
      messages.createIndex('by-date', 'dateTs')

      const ranges = db.createObjectStore('syncRanges', { keyPath: 'id', autoIncrement: true })
      ranges.createIndex('by-chat', 'chatId')
    }

    if (oldVersion < 2) {
      const searchDocs = db.createObjectStore('searchDocs', { keyPath: ['chatId', 'messageId'] })
      searchDocs.createIndex('by-date', 'dateTs')
      searchDocs.createIndex('by-chat-date', ['chatId', 'dateTs'])
    }

    if (oldVersion < 3) {
      const messages = transaction.objectStore('messages')
      messages.createIndex('by-sender-date', ['senderId', 'dateTs'])
    }
  },
})

function estimateMessageRecordBytes(message: MessageRecord) {
  return new Blob([JSON.stringify(message)]).size
}

async function summarizeChatMetrics(messageStore: IDBPObjectStore<any, ArrayLike<'messages'>, 'messages', 'readwrite'>, chatId: string) {
  const index = messageStore.index('by-chat-date')
  const range = IDBKeyRange.bound([chatId, 0], [chatId, Number.MAX_SAFE_INTEGER])
  let count = 0
  let bytes = 0
  let cursor = await index.openCursor(range)

  while (cursor) {
    count += 1
    bytes += estimateMessageRecordBytes(cursor.value as MessageRecord)
    cursor = await cursor.continue()
  }

  return { count, bytes }
}

export const browserDb = {
  async saveChats(chats: StoredChat[]) {
    const db = await dbPromise
    const tx = db.transaction('chats', 'readwrite')
    for (const chat of chats) {
      const existing = await tx.store.get(chat.id) as StoredChat | undefined
      await tx.store.put({
        ...chat,
        syncedMessageCount: existing?.syncedMessageCount ?? chat.syncedMessageCount ?? 0,
        syncedStorageBytes: existing?.syncedStorageBytes ?? chat.syncedStorageBytes ?? 0,
      })
    }
    await tx.done
  },
  async getChats(): Promise<ChatSummary[]> {
    const db = await dbPromise
    const tx = db.transaction(['chats', 'messages'], 'readwrite')
    const chatStore = tx.objectStore('chats')
    const messageStore = tx.objectStore('messages')
    const chats = await chatStore.getAll() as StoredChat[]

    const normalizedChats: ChatSummary[] = []
    for (const chat of chats) {
      const { count: actualCount, bytes: actualBytes } = await summarizeChatMetrics(messageStore, chat.id)

      if ((chat.syncedMessageCount ?? 0) !== actualCount || (chat.syncedStorageBytes ?? 0) !== actualBytes) {
        await chatStore.put({
          ...chat,
          syncedMessageCount: actualCount,
          syncedStorageBytes: actualBytes,
        })
      }

      normalizedChats.push({
        ...chat,
        syncedMessageCount: actualCount,
        syncedStorageBytes: actualBytes,
      })
    }

    await tx.done

    return normalizedChats
      .sort((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0) || a.title.localeCompare(b.title))
  },
  async saveMessages(messages: MessageRecord[]) {
    if (messages.length === 0) {
      return
    }

    const db = await dbPromise
    const tx = db.transaction(['messages', 'chats', 'searchDocs'], 'readwrite')
    const messageStore = tx.objectStore('messages')
    const chatStore = tx.objectStore('chats')
    const searchStore = tx.objectStore('searchDocs')
    const affectedChatIds = new Set<string>()

    for (const message of messages) {
      affectedChatIds.add(message.chatId)
      await messageStore.put(message)
      if (message.text.trim().length > 0) {
        await searchStore.put(message)
      }
      else {
        await searchStore.delete([message.chatId, message.messageId])
      }
    }

    for (const chatId of affectedChatIds) {
      const { count, bytes } = await summarizeChatMetrics(messageStore, chatId)
      const chat = await chatStore.get(chatId) as StoredChat | undefined
      if (!chat) {
        continue
      }
      await chatStore.put({
        ...chat,
        syncedMessageCount: count,
        syncedStorageBytes: bytes,
      })
    }

    await tx.done
  },
  async getMessagesForDay(chatId: string, startTs: number, endTs: number) {
    const db = await dbPromise
    const messages = await db.getAllFromIndex(
      'messages',
      'by-chat-date',
      IDBKeyRange.bound([chatId, startTs], [chatId, endTs]),
    ) as MessageRecord[]

    return messages
      .sort((a, b) => a.dateTs - b.dateTs || a.messageId - b.messageId)
  },
  async getMessagesBySender(senderId: string, chatIds?: string[]) {
    const db = await dbPromise
    const tx = db.transaction(['messages', 'chats'], 'readonly')
    const messageIndex = tx.objectStore('messages').index('by-sender-date')
    const chats = await tx.objectStore('chats').getAll() as StoredChat[]
    const chatMap = new Map(chats.map((chat) => [chat.id, chat.title]))
    const allowedChatIds = chatIds ? new Set(chatIds) : null
    const range = IDBKeyRange.bound([senderId, 0], [senderId, Number.MAX_SAFE_INTEGER])
    const results: SearchResultRecord[] = []
    let cursor = await messageIndex.openCursor(range, 'prev')

    while (cursor) {
      const message = cursor.value as MessageRecord
      if (!allowedChatIds || allowedChatIds.has(message.chatId)) {
        results.push({
          ...message,
          chatTitle: chatMap.get(message.chatId) ?? message.chatId,
        })
      }
      cursor = await cursor.continue()
    }

    await tx.done

    return results
  },
  async addSyncRange(
    chatId: string,
    startTs: number,
    endTs: number,
    messageCount: number,
    actualStartTs?: number,
    actualEndTs?: number,
  ) {
    const db = await dbPromise
    await db.add('syncRanges', {
      chatId,
      startTs,
      endTs,
      syncedAt: Date.now(),
      messageCount,
      actualStartTs,
      actualEndTs,
    })
  },
  async getRanges(chatId: string): Promise<SyncRangeRecord[]> {
    const db = await dbPromise
    const tx = db.transaction(['syncRanges', 'messages'], 'readwrite')
    const ranges = await tx.objectStore('syncRanges').index('by-chat').getAll(chatId) as SyncRangeRecord[]
    const messageIndex = tx.objectStore('messages').index('by-chat-date')
    const normalizedRanges: SyncRangeRecord[] = []

    for (const range of ranges) {
      let actualStartTs = range.actualStartTs
      let actualEndTs = range.actualEndTs

      if (range.messageCount > 0 && (actualStartTs === undefined || actualEndTs === undefined)) {
        const query = IDBKeyRange.bound([chatId, range.startTs], [chatId, range.endTs])
        const first = await messageIndex.openCursor(query, 'next')
        const last = await messageIndex.openCursor(query, 'prev')
        actualStartTs = first?.value?.dateTs
        actualEndTs = last?.value?.dateTs

        if (actualStartTs !== range.actualStartTs || actualEndTs !== range.actualEndTs) {
          await tx.objectStore('syncRanges').put({
            ...range,
            actualStartTs,
            actualEndTs,
          })
        }
      }

      normalizedRanges.push({
        ...range,
        actualStartTs,
        actualEndTs,
      })
    }

    await tx.done

    return normalizedRanges.sort((a, b) => b.startTs - a.startTs)
  },
  async getSearchDocuments(): Promise<SearchResultRecord[]> {
    const db = await dbPromise
    const tx = db.transaction(['searchDocs', 'messages', 'chats'], 'readwrite')
    const [storedDocsRaw, chats] = await Promise.all([
      tx.objectStore('searchDocs').getAll() as Promise<StoredSearchDoc[]>,
      tx.objectStore('chats').getAll() as Promise<StoredChat[]>,
    ])

    let storedDocs = storedDocsRaw
    if (storedDocs.length === 0) {
      const messages = await tx.objectStore('messages').getAll() as MessageRecord[]
      storedDocs = messages.filter((message) => message.text.trim().length > 0)
      for (const message of storedDocs) {
        await tx.objectStore('searchDocs').put(message)
      }
    }

    await tx.done

    const chatMap = new Map(chats.map((chat) => [chat.id, chat.title]))

    return storedDocs
      .map((message) => ({
        ...message,
        chatTitle: chatMap.get(message.chatId) ?? message.chatId,
      }))
      .sort((a, b) => b.dateTs - a.dateTs)
  },
  async clearAll() {
    const db = await dbPromise
    await Promise.all([
      db.clear('chats'),
      db.clear('messages'),
      db.clear('searchDocs'),
      db.clear('syncRanges'),
    ])
  },
}
